import { useEffect, useId, useState } from 'react';
import { BundleTreeType, BundleTreeTypes, getAllParentNodesByPageId } from '@repo/atomic-components';
import { useAppSelector, selectBundleSections } from '@/store';
import { BundleSectionsTreeNode } from '@/api';

export const DEFAULT_NEW_SECTION_NAME = 'New section';

export interface SectionItem extends Pick<BundleSectionsTreeNode, 'id' | 'name' | 'type'> {
  children?: SectionItem[];
}

/**
 *  Recursively format tree nodes to SectionItem[]
 */
const getFormattedListItems = (treeNodes: BundleSectionsTreeNode[]): SectionItem[] => {
  return treeNodes.map((item) => ({
    id: item.id,
    name: item.name,
    type: item.type,
    children: item.children ? getFormattedListItems(item.children) : undefined,
  }));
};

/**
 * Recursively remove item by id
 */
const removeItemById = (items: SectionItem[], idToRemove: string): SectionItem[] => {
  return items.filter((item) => {
    if (item.id === idToRemove) return false;
    if (item.children) item.children = removeItemById(item.children, idToRemove);
    return true;
  });
};

interface UseMoveSectionsDialogLogicProps {
  pageIds: string[];
}

export const useMoveSectionsDialogLogic = ({ pageIds }: UseMoveSectionsDialogLogicProps) => {
  const newSectionId = useId();
  const [newSectionName, setNewSectionName] = useState<string>('');
  const [newSectionType, setNewSectionType] = useState<BundleTreeType | null>(null);
  const [newSectionIndex, setNewSectionIndex] = useState<number | null>(null);
  const [newSectionCategoryId, setNewSectionCategoryId] = useState<string | null>(null);
  const [isNewSectionAdded, setIsNewSectionAdded] = useState<boolean>(false);
  const [isNewSectionNameAlreadyExistInCurrentType, setIsNewSectionNameAlreadyExistInCurrentType] =
    useState<boolean>(false);

  const [providerId, setProviderId] = useState<string | null>(null);

  const [listItems, setListItems] = useState<SectionItem[]>([]);
  const [currentListItems, setCurrentListItems] = useState<SectionItem[]>([]);
  const [currentListItemsType, setCurrentListItemsType] = useState<BundleTreeType>(BundleTreeTypes.CATEGORY);
  const [topLevelElement, setTopLevelElement] = useState<SectionItem | null>(null);
  const [showBackButton, setShowBackButton] = useState<boolean>(false);

  const bundleSections = useAppSelector(selectBundleSections);

  useEffect(() => {
    const parentNodes = getAllParentNodesByPageId(pageIds[0], bundleSections);
    if (parentNodes.provider?.node?.children && parentNodes.provider.node.children.length > 0) {
      setTopLevelElement({
        id: parentNodes.provider.node.id,
        name: parentNodes.provider.node.name,
        type: parentNodes.provider.node.type,
      });

      if (parentNodes.provider.node.type === BundleTreeTypes.PROVIDER) {
        setProviderId(parentNodes.provider.node.id);
      }

      const formattedListItems: SectionItem[] = getFormattedListItems(parentNodes.provider.node.children);
      const currentFormattedListItems = formattedListItems.filter((item) => item.type === BundleTreeTypes.CATEGORY);

      setListItems(formattedListItems);
      setCurrentListItems(currentFormattedListItems);
      setCurrentListItemsType(BundleTreeTypes.CATEGORY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewSectionClick = (index: number, overrideName?: string): boolean => {
    const targetName = overrideName?.trim() || newSectionName || DEFAULT_NEW_SECTION_NAME;

    const isElementWithTheSameNameInCurrentType = currentListItems.some(
      (item) => item.name.toLocaleLowerCase() === targetName.toLocaleLowerCase(),
    );

    if (isElementWithTheSameNameInCurrentType && !isNewSectionNameAlreadyExistInCurrentType) {
      setIsNewSectionNameAlreadyExistInCurrentType(true);
      return false;
    }

    const newItem = {
      id: newSectionId,
      name: targetName,
      type: currentListItemsType,
    } satisfies SectionItem;

    // Update in full items list
    const formattedAndUpdatedListItems = [...listItems];
    if (topLevelElement && topLevelElement.type === BundleTreeTypes.PROVIDER) {
      formattedAndUpdatedListItems.splice(index, 0, structuredClone(newItem));
    }
    if (topLevelElement && topLevelElement.type === BundleTreeTypes.CATEGORY) {
      formattedAndUpdatedListItems.map((listItem) => {
        return listItem.id === topLevelElement.id && listItem.children && listItem.children.length > 0
          ? listItem.children.splice(index, 0, structuredClone(newItem))
          : listItem;
      });
    }
    // Update current items list
    const currentFormattedAndUpdatedListItems = [...currentListItems];
    currentFormattedAndUpdatedListItems.splice(index, 0, structuredClone(newItem));

    setListItems(formattedAndUpdatedListItems);
    setCurrentListItems(currentFormattedAndUpdatedListItems);
    setNewSectionType(currentListItemsType);
    setNewSectionIndex(index);
    setIsNewSectionAdded(true);
    return true;
  };

  const moveNewSectionToIndex = (targetIndex: number) => {
    // Reorder the pending new section within the current visible list.
    // Only fires post-confirm (when `isNewSectionAdded` is true) so the
    // caller doesn't need extra guards. Keeps the full-list mirror in
    // sync since handleMove reads `newSectionIndex` off that.
    if (!isNewSectionAdded || newSectionIndex === null) return;
    if (targetIndex === newSectionIndex) return;
    const clampedIndex = Math.max(0, Math.min(targetIndex, currentListItems.length - 1));

    const currentCopy = [...currentListItems];
    const currentPos = currentCopy.findIndex((item) => item.id === newSectionId);
    if (currentPos === -1) return;
    const [movedItem] = currentCopy.splice(currentPos, 1);
    currentCopy.splice(clampedIndex, 0, movedItem);
    setCurrentListItems(currentCopy);

    // Mirror the move in the full list. When top-level element is a
    // provider we act on the top-level array; when it's a category we
    // act on that category's children (same shape as add/remove).
    setListItems((prev) => {
      const nextList = [...prev];
      if (topLevelElement && topLevelElement.type === BundleTreeTypes.PROVIDER) {
        const idx = nextList.findIndex((item) => item.id === newSectionId);
        if (idx !== -1) {
          const [moved] = nextList.splice(idx, 1);
          nextList.splice(clampedIndex, 0, moved);
        }
        return nextList;
      }
      if (topLevelElement && topLevelElement.type === BundleTreeTypes.CATEGORY) {
        return nextList.map((listItem) => {
          if (listItem.id !== topLevelElement.id || !listItem.children) return listItem;
          const children = [...listItem.children];
          const idx = children.findIndex((item) => item.id === newSectionId);
          if (idx !== -1) {
            const [moved] = children.splice(idx, 1);
            children.splice(clampedIndex, 0, moved);
          }
          return { ...listItem, children };
        });
      }
      return nextList;
    });

    setNewSectionIndex(clampedIndex);
  };

  const handleRemoveNewSectionClick = (item: SectionItem) => {
    setListItems((prevSections) => removeItemById([...prevSections], item.id));
    setCurrentListItems((prevSections) => removeItemById([...prevSections], item.id));
    setNewSectionType(null);
    setNewSectionIndex(null);
    setIsNewSectionAdded(false);
  };

  const handleOpenSubcategoriesClick = (item: SectionItem) => {
    const selectedListItem: SectionItem | undefined = listItems.find((listItem) => listItem.id === item.id);
    if (!selectedListItem || !selectedListItem.children || selectedListItem.children.length === 0) {
      console.info('Something went wrong, check listItems array!');
      return;
    }

    const currentFormattedListItems = selectedListItem.children.filter(
      (listItem) => listItem.type === BundleTreeTypes.SUB_CATEGORY,
    );

    setNewSectionCategoryId(item.id);
    setTopLevelElement(item);
    setCurrentListItems(currentFormattedListItems);
    setCurrentListItemsType(BundleTreeTypes.SUB_CATEGORY);
    setShowBackButton(true);
  };

  const handleBackToCategoriesClick = () => {
    const parentNodes = getAllParentNodesByPageId(pageIds[0], bundleSections);
    if (parentNodes.provider?.node?.children && parentNodes.provider.node.children.length > 0) {
      setTopLevelElement({
        id: parentNodes.provider.node.id,
        name: parentNodes.provider.node.name,
        type: parentNodes.provider.node.type,
      });
    }
    const currentFormattedListItems = listItems.filter((item) => item.type === BundleTreeTypes.CATEGORY);

    setNewSectionCategoryId(null);
    setCurrentListItems(currentFormattedListItems);
    setCurrentListItemsType(BundleTreeTypes.CATEGORY);
    setShowBackButton(false);
  };

  return {
    newSectionId,
    newSectionName,
    newSectionType,
    newSectionIndex,
    newSectionCategoryId,
    setNewSectionName,
    isNewSectionAdded,
    providerId,
    listItems,
    setListItems,
    currentListItems,
    setCurrentListItems,
    currentListItemsType,
    setCurrentListItemsType,
    topLevelElement,
    setTopLevelElement,
    showBackButton,
    setShowBackButton,
    isNewSectionNameAlreadyExistInCurrentType,
    handleAddNewSectionClick,
    handleRemoveNewSectionClick,
    handleOpenSubcategoriesClick,
    handleBackToCategoriesClick,
    setIsNewSectionNameAlreadyExistInCurrentType,
    moveNewSectionToIndex,
  };
};
